import React, { useEffect, useState } from 'react';
import api from '../config/api';

interface TopActiveMember {
  user: {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
    section: string;
    photo: string | null;
  } | null;
  stats: {
    total_likes: number;
    total_comments: number;
    total_score: number;
  } | null;
  messages: string[];
  is_tie?: boolean;
  tied_members_count?: number;
}

const SECTION_BADGES: Record<string, { label: string; icon: string; className: string }> = {
  VCN: { label: 'VCN', icon: '🎓', className: 'bg-blue-100 text-blue-800' },
  HDR: { label: 'HDR', icon: '🏛️', className: 'bg-purple-100 text-purple-800' },
  SANTE: { label: 'SANTÉ', icon: '🩺', className: 'bg-emerald-100 text-emerald-800' },
};

export const TopBar: React.FC = () => {
  const [data, setData] = useState<TopActiveMember | null>(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopActiveMember();
    // Rafraîchir toutes les 5 minutes
    const interval = setInterval(fetchTopActiveMember, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (data?.messages && data.messages.length > 0) {
      // Changer de message toutes les 8 secondes
      const messageInterval = setInterval(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % data.messages.length);
      }, 8000);
      return () => clearInterval(messageInterval);
    }
  }, [data?.messages]);

  const fetchTopActiveMember = async () => {
    try {
      const response = await api.get('/top-active-member/');
      setData(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération du membre le plus actif:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data || !data.user || !data.stats) {
    return null;
  }

  const sectionInfo = SECTION_BADGES[data.user.section] || SECTION_BADGES.VCN;
  const currentMessage = data.messages[currentMessageIndex] || data.messages[0];

  return (
    <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-green-600 via-green-500 to-green-600 text-white py-2 shadow-lg overflow-hidden z-[60] border-b-2 border-green-700 h-[50px] flex items-center w-full max-w-full">
      {/* Effet de brillance animé */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full animate-shimmer"></div>
      
      <div className="relative max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 h-full w-full">
        <div className="flex items-center justify-between sm:justify-center gap-1 sm:gap-2 md:gap-4 h-full w-full">
          {/* Photo de profil */}
          {data.user.photo ? (
            <img
              src={data.user.photo}
              alt={`${data.user.first_name} ${data.user.last_name}`}
              className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-full border-2 border-white/90 shadow-lg object-cover ring-2 ring-green-300 flex-shrink-0"
            />
          ) : (
            <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-full border-2 border-white/90 shadow-lg bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-[10px] sm:text-xs md:text-sm ring-2 ring-green-300 flex-shrink-0">
              {data.user.first_name?.[0] || data.user.username[0].toUpperCase()}
            </div>
          )}

          {/* Message défilant */}
          <div className="flex-1 text-center overflow-hidden relative min-w-0 max-w-full">
            <div className="flex items-center justify-center gap-0.5 sm:gap-1 md:gap-2 w-full">
              <span className="text-xs sm:text-sm md:text-lg font-bold animate-bounce drop-shadow-lg flex-shrink-0 hidden xs:inline">🎉</span>
              <div className="relative w-full overflow-hidden max-w-full">
                <div
                  key={currentMessageIndex}
                  className="animate-slide-in-out text-[10px] xs:text-xs sm:text-sm md:text-base font-semibold drop-shadow-md truncate sm:whitespace-nowrap"
                >
                  {currentMessage}
                </div>
              </div>
              <span className="text-xs sm:text-sm md:text-lg font-bold animate-bounce drop-shadow-lg flex-shrink-0 hidden xs:inline">🎉</span>
            </div>
          </div>

          {/* Badge section */}
          <div className={`hidden sm:flex items-center space-x-1 px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold ${sectionInfo.className} shadow-lg border border-white/30 flex-shrink-0`}>
            <span>{sectionInfo.icon}</span>
            <span className="hidden md:inline">{sectionInfo.label}</span>
          </div>
          
          {/* Indicateur d'égalité si plusieurs membres ex aequo */}
          {data.is_tie && data.tied_members_count && data.tied_members_count > 1 && (
            <div className="hidden lg:flex items-center space-x-1 px-2 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-white/20 text-white border border-white/30 flex-shrink-0">
              <span>👥</span>
              <span>{data.tied_members_count} ex aequo</span>
            </div>
          )}
        </div>
      </div>

      {/* Styles d'animation personnalisés */}
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%) skewX(-12deg);
          }
          100% {
            transform: translateX(200%) skewX(-12deg);
          }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        @keyframes slide-in-out {
          0% {
            opacity: 0;
            transform: translateX(30px);
          }
          10% {
            opacity: 1;
            transform: translateX(0);
          }
          90% {
            opacity: 1;
            transform: translateX(0);
          }
          100% {
            opacity: 0;
            transform: translateX(-30px);
          }
        }
        .animate-slide-in-out {
          animation: slide-in-out 8s ease-in-out infinite;
        }
        @media (max-width: 640px) {
          .animate-slide-in-out {
            animation: none;
            opacity: 1;
            transform: none;
          }
        }
      `}</style>
    </div>
  );
};

